import { GetFaqCategoriesQuery } from '@/api/sanity/generated';

export type FaqCategory = NonNullable<GetFaqCategoriesQuery['allFaqCategory']>;
type FaqCategoryItem = NonNullable<FaqCategory[number]>;

export type OrderedFaqCategories = {
	productsAndServices: FaqCategory;
	shopping: FaqCategory;
	settings: FaqCategory;
	other?: FaqCategory;
};

const isTestEnv =
	process.env.NEXT_PUBLIC_TARGET_ENV === 'accept' ||
	process.env.NEXT_PUBLIC_TARGET_ENV === 'nightly';

const mapIdConfig = (
	isTestEnv
		? {
				productsAndServices: [
					'2geVXnb3nbSCZ72kDp0vMr',
					'1KoTGhGVcmlZQQIr6IZbwo',
					'2Czz8zT5oJbs5GGoDaMqvN',
					'6nV2Tpb7PwTJiS4RSLte7Z',
					'2igTYT1lreIJvrhQkV5TWI',
					'HEtFwZ5YtBqEHx3MYRtOr',
					'2tVFp1Ng5ctS7b0HvvN1uY',
				],
				shopping: [
					'7h4Ma1zO92a1Nd3sW7GxwY',
					'6z4g7uCMkhmFMTBTj68bDm',
					'3WbA73rElE1mf9cabNICXp',
					'1raPmdn0YTj6GW37nZiJNN',
					'6WAvU3gZ8gpZYTSxEFtUen',
					'8eb44a6a-b951-4e35-b8ae-4a9caa0b788a',
				],
				settings: ['604inmauqbZYcOgNxyS33Q', '7JbroiixAEgFYhfYLt6mp'],
				other: ['1mzXo6lfsyNNX2W3XhKMjd', '2ZrywVGhSCNTn97nwrzFGN'],
			}
		: {
				productsAndServices: [
					'3mOvmE8yTjrrDsZJJyX4tJ',
					'4nQV5zahsGsZ7TiGXSSWbZ',
					'7iCsxLitHoUqqRyE9ULjwV',
					'6nV2Tpb7PwTJiS4RSLte7Z',
					'HEtFwZ5YtBqEHx3MYRtOr',
					'2tVFp1Ng5ctS7b0HvvN1uY',
				],
				shopping: [
					'7h4Ma1zO92a1Nd3sW7GxwY',
					'6z4g7uCMkhmFMTBTj68bDm',
					'3WbA73rElE1mf9cabNICXp',
					'4Q2CmNtyqNsLWG3t61wgJN',
					'6WAvU3gZ8gpZYTSxEFtUen',
					'63ee7e4b-4a91-4c9a-a3ae-5208442b2d4c',
				],
				settings: ['604inmauqbZYcOgNxyS33Q', '7JbroiixAEgFYhfYLt6mp'],
				other: ['1mzXo6lfsyNNX2W3XhKMjd', '2ZrywVGhSCNTn97nwrzFGN'],
			}
) satisfies {
	productsAndServices: string[];
	shopping: string[];
	settings: string[];
	other: string[];
};

const mapSKIdConfig = {
	productsAndServices: ['6Iyu1doyxlpMLwhkPsujmK', '6BfMs6m5qGEYn3qsOWCBbG'],
	shopping: ['2DwUXOK4Tws9NuPXiKBTbl', '12vL2KlrTy7tqbc1qWLwI4'],
	settings: ['61XykEPmax93RXyx3aCKgp', '4Gp1b4tpRj45qx3ORCzNXM'],
} satisfies {
	productsAndServices: string[];
	shopping: string[];
	settings: string[];
};

const mapHUIdConfig = {
	productsAndServices: [
		'd97454e4-3ad8-449f-a190-5f2d7b592a5b',
		'2dd27096-53ce-4787-8e05-b4f818d9032a',
	],
	shopping: ['d3b1f108-a23b-4a72-96bf-beac131a8b2a', '27069180-625f-45e9-83da-c11ab277b857'],
	settings: ['3a390f22-231f-48dd-a880-4c7adbae869a', '6fb0b14d-d7bb-4b6d-a302-6c9ca75cbe50'],
} satisfies {
	productsAndServices: string[];
	shopping: string[];
	settings: string[];
};

function sortCategoriesByIds(ids: string[], categories: FaqCategory): FaqCategory {
	const byId = new Map(
		categories
			.filter((category): category is FaqCategoryItem & { _id: string } =>
				Boolean(category && category._id)
			)
			.map((category) => [category._id!, category])
	);

	return ids.map((id) => byId.get(id)).filter(Boolean) as FaqCategory;
}

export function orderFaqCategories(
	categories: NonNullable<GetFaqCategoriesQuery['allFaqCategory']>
): OrderedFaqCategories {
	return {
		productsAndServices: sortCategoriesByIds(mapIdConfig.productsAndServices, categories),
		shopping: sortCategoriesByIds(mapIdConfig.shopping, categories),
		settings: sortCategoriesByIds(mapIdConfig.settings, categories),
		other: mapIdConfig.other ? sortCategoriesByIds(mapIdConfig.other, categories) : undefined,
	};
}

export function orderSKFaqCategories(
	categories: NonNullable<GetFaqCategoriesQuery['allFaqCategory']>
): OrderedFaqCategories {
	return {
		productsAndServices: sortCategoriesByIds(mapSKIdConfig.productsAndServices, categories),
		shopping: sortCategoriesByIds(mapSKIdConfig.shopping, categories),
		settings: sortCategoriesByIds(mapSKIdConfig.settings, categories),
	};
}

export function orderHUFaqCategories(
	categories: NonNullable<GetFaqCategoriesQuery['allFaqCategory']>
): OrderedFaqCategories {
	return {
		productsAndServices: sortCategoriesByIds(mapHUIdConfig.productsAndServices, categories),
		shopping: sortCategoriesByIds(mapHUIdConfig.shopping, categories),
		settings: sortCategoriesByIds(mapHUIdConfig.settings, categories),
	};
}
